"use client"
import { useState, useMemo, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import theme from "@/theme/theme";
import { Stack, Typography, Collapse, List, ListItem, ListItemText, ListItemButton, ListItemIcon, Checkbox, IconButton, ClickAwayListener, Box } from "@mui/material";
import ClearIcon from '@mui/icons-material/Clear';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import arrow from "@/assets/images/icons/arrow.webp"
import { portfolioItems } from "@/data/portfolio";
import type { PortfolioItem } from "@/types/portfolio";
import SideTabbedButton from "@/components/generic/SideTabbedButton";
import ProjectCard from "@/components/portfolio/ProjectCard";
import { getCarouselProjectById } from "@/data/portfolioProjectsData";


const tools = ["UI/UX", "Frontend", "Backend", "Blockchain", "DevOps", "DB/Storage"];
const types = ["DeFi", "RWA", "NFT", "L1/L2", "AI", "Social"];

const PortfolioSearchableList = () => {
    const [openTools, setOpenTools] = useState<boolean>(false);
    const [openTypes, setOpenTypes] = useState<boolean>(false);
    
    const [selectedTools, setSelectedTools] = useState<string[]>([]);
    const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
    
    const [appliedTools, setAppliedTools] = useState<string[]>([]);
    const [appliedTypes, setAppliedTypes] = useState<string[]>([]);

    useEffect(() => {
        if (!openTools && !openTypes) {
            setAppliedTools(selectedTools);
            setAppliedTypes(selectedTypes);
        }
    }, [openTools, openTypes, selectedTools, selectedTypes]);

    const handleToggleTool = (tool: string) => {
        setSelectedTools(prev => prev.includes(tool) ? prev.filter(t => t !== tool) : [...prev, tool]);
    };

    const handleToggleType = (type: string) => {
        setSelectedTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
    };

    const filteredItems = useMemo(() => {
        const filtered = portfolioItems.filter(item => {
            const matchTools = appliedTools.length === 0 || item.tools.some(tool => appliedTools.includes(tool));
            const matchTypes = appliedTypes.length === 0 || item.type.some(type => appliedTypes.includes(type));
            return matchTools && matchTypes;
        });

        const useCustomOrder = appliedTools.length === 0 && appliedTypes.length === 0;
        if (useCustomOrder) {
            return filtered;
        }

        return filtered.slice().sort((a, b) => a.title.localeCompare(b.title));
    }, [appliedTools, appliedTypes]);

    return (
        <Stack width={{ xs: "90%", lg: "70%" }} alignItems="center" marginX="auto" gap={2}>
            <ClickAwayListener onClickAway={() => { setOpenTypes(false); setOpenTools(false); }}>
                <Stack 
                    width="100%" 
                    direction={{ xs: "column", lg: "row" }} 
                    alignItems="start" 
                    justifyContent="center" 
                    gap={2} 
                    sx={{ position: "relative", zIndex: 20 }}
                >
                    <Stack width={{ xs: "100%", lg: "50%" }} sx={{ position: "relative", zIndex: openTypes ? 100 : 1 }}>
                        <Stack
                            role="button"
                            tabIndex={0}
                            aria-expanded={openTypes}
                            aria-haspopup="listbox"
                            aria-controls="portfolio-types-list"
                            onClick={() => setOpenTypes(!openTypes)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenTypes(!openTypes); } }}
                            direction="row" alignItems="center" justifyContent="space-between" padding={1.5} borderRadius={2}
                            sx={{
                                border: "1px solid #6F6F6F",
                                backgroundColor: "#0F0F0F4D",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                "&:hover": { borderColor: "#FFFFFF" }
                            }}
                        >
                            {selectedTypes.length > 0 ? (
                                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                                    {selectedTypes.map((type, index) => (
                                        <Stack key={`type-${index}`} direction="row" alignItems="center" gap={1} sx={{ border: "0.3px solid #656565", backgroundColor: "#0F0F0F", borderRadius: 1, px: 1, py: 0.5 }}>
                                            <Typography variant="subtitle2" fontWeight={400}>{type}</Typography>
                                            <IconButton size="small" onClick={(e) => { 
                                                e.stopPropagation(); 
                                                handleToggleType(type); 
                                                if(!openTypes && !openTools) setAppliedTypes(prev => prev.filter(t => t !== type));
                                            }} sx={{ bgcolor: "#7F7E7E", p: 0.25, "&:hover": { opacity: 0.8 } }}>
                                                <ClearIcon sx={{ fontSize: "0.8rem" }} />
                                            </IconButton>
                                        </Stack>
                                    ))}
                                </Stack>
                            ) : (
                                <Typography variant="subtitle1" fontWeight={400} color={theme.palette.text.secondary}>Type of project</Typography>
                            )}
                            <KeyboardArrowDownIcon sx={{ transition: "200ms", transform: openTypes ? "rotate(180deg)" : "0" }} />
                        </Stack>
                        <Collapse in={openTypes} sx={{ position: "absolute", top: "105%", left: 0, width: "100%", bgcolor: "#0B0B0B", border: "1px solid #2F2F2F", borderRadius: 2, boxShadow: "0px 10px 30px rgba(0,0,0,0.8)", zIndex: 110 }}>
                            <List id="portfolio-types-list" role="listbox" dense sx={{ p: 1 }}>
                                {types.map((type) => (
                                    <ListItem key={type} disablePadding role="option" aria-selected={selectedTypes.includes(type)}>
                                        <ListItemButton onClick={() => handleToggleType(type)} sx={{ borderRadius: 1 }}>
                                            <ListItemIcon sx={{ minWidth: 0 }}><Checkbox checked={selectedTypes.includes(type)} disableRipple slotProps={{ input: { 'aria-label': type } }} sx={{ color: theme.palette.text.secondary, "&.Mui-checked": { color: theme.palette.text.primary } }} /></ListItemIcon>
                                            <ListItemText primary={type} />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        </Collapse>
                    </Stack>
                    <Stack width={{ xs: "100%", lg: "50%" }} sx={{ position: "relative", zIndex: openTools ? 100 : 1 }}>
                        <Stack
                            role="button"
                            tabIndex={0}
                            aria-expanded={openTools}
                            aria-haspopup="listbox"
                            aria-controls="portfolio-tools-list"
                            onClick={() => setOpenTools(!openTools)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenTools(!openTools); } }}
                            direction="row" alignItems="center" justifyContent="space-between" padding={1.5} borderRadius={2}
                            sx={{
                                border: "1px solid #6F6F6F",
                                backgroundColor: "#0F0F0F4D",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                "&:hover": { borderColor: "#FFFFFF" }
                            }}
                        >
                            {selectedTools.length > 0 ? (
                                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                                    {selectedTools.map((tool, index) => (
                                        <Stack key={`tool-${index}`} direction="row" alignItems="center" gap={1} sx={{ border: "0.3px solid #656565", backgroundColor: "#0F0F0F", borderRadius: 1, px: 1, py: 0.5 }}>
                                            <Typography variant="subtitle2" fontWeight={400}>{tool}</Typography>
                                            <IconButton size="small" onClick={(e) => { 
                                                e.stopPropagation(); 
                                                handleToggleTool(tool); 
                                                if(!openTools && !openTypes) setAppliedTools(prev => prev.filter(t => t !== tool));
                                            }} sx={{ bgcolor: "#7F7E7E", p: 0.25, "&:hover": { opacity: 0.8 } }}>
                                                <ClearIcon sx={{ fontSize: "0.8rem" }} />
                                            </IconButton>
                                        </Stack>
                                    ))}
                                </Stack>
                            ) : (
                                <Typography variant="subtitle1" fontWeight={400} color={theme.palette.text.secondary}>Type of tool</Typography>
                            )}
                            <KeyboardArrowDownIcon sx={{ transition: "200ms", transform: openTools ? "rotate(180deg)" : "0" }} />
                        </Stack>
                        <Collapse in={openTools} sx={{ position: "absolute", top: "105%", left: 0, width: "100%", bgcolor: "#0B0B0B", border: "1px solid #2F2F2F", borderRadius: 2, boxShadow: "0px 10px 30px rgba(0,0,0,0.8)", zIndex: 110 }}>
                            <List id="portfolio-tools-list" role="listbox" dense sx={{ p: 1 }}>
                                {tools.map((tool) => (
                                    <ListItem key={tool} disablePadding role="option" aria-selected={selectedTools.includes(tool)}>
                                        <ListItemButton onClick={() => handleToggleTool(tool)} sx={{ borderRadius: 1 }}>
                                            <ListItemIcon sx={{ minWidth: 0 }}><Checkbox checked={selectedTools.includes(tool)} disableRipple slotProps={{ input: { 'aria-label': tool } }} sx={{ color: theme.palette.text.secondary, "&.Mui-checked": { color: theme.palette.text.primary } }} /></ListItemIcon>
                                            <ListItemText primary={tool} />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        </Collapse>
                    </Stack>
                </Stack>
            </ClickAwayListener>
            {filteredItems.length === 0 ? (
                <Box sx={{ width: "100%", mt: 45 }}></Box>
            ) : (
                filteredItems.map((item: PortfolioItem) => {
                    const hasLink = item.link.trim().length > 0;
                    const project = getCarouselProjectById(item.carouselProjectId);

                    if (!project) return null;

                    return (
                    <Stack key={item.id} width="100%" height="fit-content" direction={{ xs: "column", lg: "row" }} alignItems="stretch" justifyContent="space-between" sx={{
                        borderRadius: { xs: 2, lg: 2 },
                        border: "1px solid #2F2F2F",
                        background: "#0F0F0F4D",
                        overflow: "hidden",
                        padding: { xs: 0, lg: 2 },
                        gap: { xs: 2, lg: 6 }
                    }}>
                        <Stack
                            width={{ xs: "100%", lg: "40%" }}
                            height={{ xs: "250px", sm: "320px", md: "340px", lg: "240px", xl: "280px" }}
                            alignItems="center"
                            justifyContent="center"
                        >
                            <ProjectCard project={project} />
                        </Stack>
                        <Stack width={{ xs: "100%", lg: "60%" }} flexGrow={1} justifyContent="space-between" paddingX={{ xs: 2, lg: 0 }} paddingBottom={{ xs: 2, lg: 0 }} gap={{ xs: 3, lg: 0 }}>
                            <Stack height="fit-content" width="100%" direction="row" alignItems="center" flexWrap="wrap" gap={{ xs: 0.5, lg: 1 }}>
                                {item.tools.map((tool, index) => (
                                    <Typography key={`tool-${index}`} variant="body2" fontWeight={400} sx={{
                                        backgroundColor: theme.palette.brand.mgsBlue1.main,
                                        paddingY: 1,
                                        paddingX: { xs: 1.5, lg: 2 },
                                        borderRadius: 10,
                                        fontSize: { xs: "0.7rem !important", lg: "unset !important" },
                                    }}>{tool}</Typography>
                                ))}
                            </Stack>
                            <Stack gap={1}>
                                <Typography variant="h5" fontWeight={500}>{item.title}</Typography>
                                <Typography variant="body2" fontWeight={400} color={theme.palette.text.secondary}>{item.description}</Typography>
                            </Stack>
                            <Stack width="100%" direction={{ xs: "column", lg: "row" }} justifyContent="space-between" alignItems="center" gap={{ xs: 4, lg: 2 }}>
                                <Stack height="fit-content" width={{ xs: "100%", lg: "fit-content" }} direction="row" alignItems="center" justifyContent={{ xs: "start", lg: "center" }} flexWrap="wrap" gap={1}>
                                    {item.type.map((type, index) => (
                                        <Typography key={`type-${index}`} variant="body2" fontWeight={400} sx={{
                                            backgroundColor: "#0F0F0F",
                                            border: "1px solid #434343",
                                            boxShadow: "0px 2px 0px 0px #434343",
                                            paddingY: 1,
                                            paddingX: 2,
                                            borderRadius: 10,
                                        }}>{type}</Typography>
                                    ))}
                                </Stack>
                                <Stack width="fit-content" justifyContent="end" display={{ xs: "none", lg: "block" }}>
                                    {hasLink ? (
                                        <Link
                                            href={item.link.trim()}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ textDecoration: "none" }}
                                        >
                                            <SideTabbedButton paddingX={18} hoverShiftX={1.5} hoverShiftY={-1}>
                                                <div className="flex items-center justify-center gap-1">
                                                    <Typography component="span" variant="h6" marginRight={1}>
                                                        Visit website
                                                    </Typography>
                                                    <span className="arrow-clip">
                                                        <span className="arrow-slide">
                                                            <Image src={arrow} alt="arrow animation" width={20} height={20} style={{ display: "block" }} />
                                                        </span>
                                                    </span>
                                                </div>
                                            </SideTabbedButton>
                                        </Link>
                                    ) : (
                                        <SideTabbedButton paddingX={18} hoverShiftX={1.5} hoverShiftY={-1} disabled>
                                            <div className="flex items-center justify-center gap-1">
                                                <Typography component="span" variant="h6" marginRight={1}>
                                                    Coming soon
                                                </Typography>
                                            </div>
                                        </SideTabbedButton>
                                    )}
                                </Stack>
                                <Stack width="100%" justifyContent="center" display={{ xs: "block", lg: "none" }}>
                                    {hasLink ? (
                                        <Link
                                            href={item.link.trim()}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ textDecoration: "none" }}
                                        >
                                            <SideTabbedButton fullWidth paddingX={18} hoverShiftX={1.5} hoverShiftY={-1}>
                                                <div className="flex items-center justify-center gap-1">
                                                    <Typography component="span" variant="h6" marginRight={1}>
                                                        Visit website
                                                    </Typography>
                                                    <span className="arrow-clip">
                                                        <span className="arrow-slide">
                                                            <Image src={arrow} alt="arrow animation" width={20} height={20} style={{ display: "block" }} />
                                                        </span>
                                                    </span>
                                                </div>
                                            </SideTabbedButton>
                                        </Link>
                                    ) : (
                                        <SideTabbedButton fullWidth paddingX={18} hoverShiftX={1.5} hoverShiftY={-1} disabled>
                                            <div className="flex items-center justify-center gap-1">
                                                <Typography component="span" variant="h6" marginRight={1}>
                                                    Coming soon
                                                </Typography>
                                            </div>
                                        </SideTabbedButton>
                                    )}
                                </Stack>
                            </Stack>
                        </Stack>
                    </Stack>
                    );
                })
            )}
        </Stack>
    );
}

export default PortfolioSearchableList;